import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { connectionColor } from "../../utils/connectionColor";

const useStyles = makeStyles(theme => ({
	stage: { color: theme.palette.primary.main, fontWeight: 600 },
	ticket: {
		position: "relative",
        borderRadius: 14,
        margin: "4px 6px",
        width: "calc(100% - 12px)",
        background: theme.palette.background.paper,
        overflow: "hidden",
        minHeight: 84,
        padding: "12px 12px 24px 16px",
        "&.Mui-selected": { background: theme.app.tint },
        "& .MuiListItemText-root": { minWidth: 0 },
        [theme.breakpoints.down("sm")]: {
          minHeight: 72, padding: "7px 12px 20px",
          "& .MuiListItemAvatar-root": { minWidth: 44 },
          "& .MuiAvatar-root": { width: 34, height: 34 },
          "& .MuiListItemText-root": { margin: 0 },
          "& .MuiTypography-body2": { fontSize: 12, lineHeight: "18px" },
          "& $contactNameWrapper:first-child > .MuiTypography-root:first-child": { fontWeight: 600, fontSize: 13 },
          "& $contactLastMessage": { paddingRight: 8 },
          "& $userTag": { bottom: 5, marginRight: 0, lineHeight: "14px", fontSize: 9 },
          "& $badgeStyle": { height: 18, minWidth: 18, fontSize: 10 },
        },
	},

	pendingTicket: {
		cursor: "unset",
        [theme.breakpoints.down("sm")]: {
          paddingRight: 88,
          "& $acceptButton": { left: "auto", right: 12, top: 9, minHeight: 32, padding: "4px 8px", fontSize: 11 },
        },
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		justifyContent: "space-between",
        gap: 8,
        minWidth: 0,
        alignItems: "center",
        "& > .MuiTypography-root": { minWidth: 0 },
	},

	lastMessageTime: {
		justifySelf: "flex-end",
        flexShrink: 0,
        fontSize: 11,
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 0,
		marginLeft: 0,
        "& .MuiBadge-badge": { position: "static", transform: "none", fontSize: 10 },
	},

	contactLastMessage: {
		paddingRight: 20,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: theme.palette.primary.main,
	},

	acceptButton: {
		position: "absolute",
		left: "50%",
	},

	ticketQueueColor: {
		flex: "none",
		width: "4px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	userTag: {
		position: "absolute",
		marginRight: 5,
		right: 12,
		bottom: 5,
		background: "#2576D2",
		color: "#ffffff",
		border: "1px solid transparent",
		padding: 1,
		paddingLeft: 5,
		paddingRight: 5,
		borderRadius: 10,
		fontSize: "10px",
        maxWidth: "calc(100% - 80px)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const whatsappColor = ticket.emailChannel ? "#3567a6" : connectionColor(ticket.whatsappId || ticket.whatsapp?.id);
	const connectionName = ticket.emailChannel ? "E-mail" : ticket.whatsapp?.name || "WhatsApp";

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={`${connectionName} · ${ticket.queue?.name || "Sem fila"}`}
				>
					<span
						style={{ backgroundColor: whatsappColor }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<Avatar src={ticket?.contact?.profilePicUrl} alt={ticket?.contact?.name}>{(ticket?.contact?.name || "?").trim().split(/\s+/).slice(0, 2).map(part => part[0]).join("").toUpperCase()}</Avatar>
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
							>
								{ticket.contact.name}
							</Typography>
							{ticket.status === "closed" && (
								<Badge
									className={classes.closedBadge}
									badgeContent={"Finalizado"}
									color="primary"
								/>
							)}
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
										<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
									) : (
										<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
							{(ticket.whatsappId || ticket.emailChannel) && (
								<span className={classes.userTag} style={{ backgroundColor: whatsappColor, borderColor: whatsappColor }} title={`Conexão: ${connectionName}`} aria-label={`Canal: ${connectionName}`}>{connectionName}</span>
							)}
						</span>
					}
					secondary={
						<span className={classes.contactNameWrapper}>
							<Typography
								className={classes.contactLastMessage}
								noWrap
								component="span"
								variant="body2"
								color="textSecondary"
							>
								<span title="Etapa do Kanban" className={classes.stage}>{ticket.kanbanStage?.name || "Entrada"} · </span>
                {ticket.lastMessage ? (
									<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
								) : (
									<br />
								)}
							</Typography>

							<Badge
								className={classes.newMessagesCount}
								badgeContent={ticket.unreadMessages}
								classes={{
									badge: classes.badgeStyle,
								}}
							/>
						</span>
					}
				/>
				{ticket.status === "pending" && (
					<ButtonWithSpinner
						color="primary"
						variant="contained"
						className={classes.acceptButton}
						size="small"
						loading={loading}
						onClick={e => handleAcepptTicket(ticket.id)}
					>
						{i18n.t("ticketsList.buttons.accept")}
					</ButtonWithSpinner>
				)}
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;

